define('services/firebase.service',[], function() {
    /**
     * Firebase Service - Capacitor Migration
     * 
     * Provides a unified interface for Firebase features using Capacitor plugins.
     * Falls back to web Firebase API when running in browser.
     */
    
    var FirebaseService = {
        
        /**
         * Check if running on native platform (Capacitor)
         */
        isNative: function() {
            return window.Capacitor && window.Capacitor.isNativePlatform();
        },
        
        /**
         * Check if Capacitor Push Notifications plugin is available
         */
        hasCapacitorPush: function() {
            return window.Capacitor && 
                   window.Capacitor.Plugins && 
                   window.Capacitor.Plugins.PushNotifications;
        },
        
        /**
         * Check if Capacitor App plugin is available (for badge)
         */
        hasCapacitorApp: function() {
            return window.Capacitor && 
                   window.Capacitor.Plugins && 
                   window.Capacitor.Plugins.App;
        },
        
        /**
         * Set badge number (replaces FirebasePlugin.setBadgeNumber)
         * @param {number} count - Badge count (0 to clear)
         */
        setBadgeNumber: function(count) {
            if (this.hasCapacitorApp()) {
                var App = window.Capacitor.Plugins.App;
                App.setBadge({ count: count || 0 }).catch(function(error) {
                    console.error('Failed to set badge:', error);
                });
            }
        },
        
        /**
         * Register for push notifications and get token
         * @param {Function} onTokenCallback - Callback with token
         * @param {Function} onErrorCallback - Error callback
         */
        registerPush: function(onTokenCallback, onErrorCallback) {
            var self = this;
            
            if (this.hasCapacitorPush()) {
                var PushNotifications = window.Capacitor.Plugins.PushNotifications;
                
                // Check permissions
                PushNotifications.checkPermissions().then(function(permStatus) {
                    if (permStatus.receive === 'prompt') {
                        return PushNotifications.requestPermissions();
                    }
                    return permStatus;
                }).then(function(permStatus) {
                    if (permStatus.receive !== 'granted') {
                        if (onErrorCallback) {
                            onErrorCallback(new Error('Push notification permission denied'));
                        }
                        return;
                    }
                    
                    // Register for push
                    return PushNotifications.register();
                }).then(function() {
                    // Listen for registration token
                    PushNotifications.addListener('registration', function(token) {
                        if (onTokenCallback) {
                            onTokenCallback(token.value);
                        }
                    });
                    
                    // Listen for registration errors
                    PushNotifications.addListener('registrationError', function(error) {
                        console.error('Push registration error:', error);
                        if (onErrorCallback) {
                            onErrorCallback(error);
                        }
                    });
                }).catch(function(error) {
                    console.error('Push registration failed:', error);
                    if (onErrorCallback) {
                        onErrorCallback(error);
                    }
                });
            } else {
                if (onErrorCallback) {
                    onErrorCallback(new Error('Push notifications not available'));
                }
            }
        },
        
        /**
         * Listen for push notification token refresh
         * @param {Function} onTokenCallback - Callback with new token
         * @param {Function} onErrorCallback - Error callback
         */
        onTokenRefresh: function(onTokenCallback, onErrorCallback) {
            if (this.hasCapacitorPush()) {
                var PushNotifications = window.Capacitor.Plugins.PushNotifications;
                
                // Listen for registration token (fires on refresh too)
                PushNotifications.addListener('registration', function(token) {
                    if (onTokenCallback) {
                        onTokenCallback(token.value);
                    }
                });
                
                // Listen for registration errors
                PushNotifications.addListener('registrationError', function(error) {
                    console.error('Token registration error:', error);
                    if (onErrorCallback) {
                        onErrorCallback(error);
                    }
                });
                
                // Check permissions and register if needed
                PushNotifications.checkPermissions().then(function(permStatus) {
                    if (permStatus.receive === 'prompt') {
                        return PushNotifications.requestPermissions();
                    }
                    return permStatus;
                }).then(function(permStatus) {
                    if (permStatus.receive === 'granted') {
                        return PushNotifications.register();
                    } else {
                        if (onErrorCallback) {
                            onErrorCallback(new Error('Push notification permission denied'));
                        }
                    }
                }).catch(function(error) {
                    console.error('Registration failed:', error);
                    if (onErrorCallback) {
                        onErrorCallback(error);
                    }
                });
            } else {
                if (onErrorCallback) {
                    onErrorCallback(new Error('Push notifications not available'));
                }
            }
        },
        
        /**
         * Check if app has push notification permission
         * @param {Function} callback - Callback with permission status
         */
        hasPermission: function(callback) {
            if (this.hasCapacitorPush()) {
                var PushNotifications = window.Capacitor.Plugins.PushNotifications;
                PushNotifications.checkPermissions().then(function(permStatus) {
                    if (callback) {
                        callback(permStatus.receive === 'granted');
                    }
                }).catch(function(error) {
                    console.error('Permission check failed:', error);
                    if (callback) {
                        callback(false);
                    }
                });
            } else {
                if (callback) {
                    callback(false);
                }
            }
        },
        
        /**
         * Request push notification permission
         * @param {Function} callback - Callback with permission status
         */
        grantPermission: function(callback) {
            if (this.hasCapacitorPush()) {
                var PushNotifications = window.Capacitor.Plugins.PushNotifications;
                PushNotifications.requestPermissions().then(function(permStatus) {
                    if (callback) {
                        callback(permStatus.receive === 'granted');
                    }
                }).catch(function(error) {
                    console.error('Permission request failed:', error);
                    if (callback) {
                        callback(false);
                    }
                });
            } else {
                if (callback) {
                    callback(false);
                }
            }
        },
        
        /**
         * Listen for push notification messages (replaces FirebasePlugin.onMessageReceived)
         * @param {Function} onMessageCallback - Callback with notification data
         * @param {Function} onErrorCallback - Error callback
         */
        onMessageReceived: function(onMessageCallback, onErrorCallback) {
            if (this.hasCapacitorPush()) {
                var PushNotifications = window.Capacitor.Plugins.PushNotifications;
                
                // Listen for foreground notifications
                PushNotifications.addListener('pushNotificationReceived', function(notification) {
                    var transformedNotification = {
                        type: notification.data && notification.data.type ? notification.data.type : 'unknown',
                        tap: false,
                        data: notification.data || {},
                        title: notification.title || '',
                        body: notification.body || ''
                    };
                    
                    if (onMessageCallback) {
                        onMessageCallback(transformedNotification);
                    }
                });
                
                // Listen for background/tapped notifications
                PushNotifications.addListener('pushNotificationActionPerformed', function(actionResult) {
                    var notification = actionResult.notification;
                    
                    var transformedNotification = {
                        type: notification.data && notification.data.type ? notification.data.type : 'unknown',
                        tap: true,
                        data: notification.data || {},
                        title: notification.title || '',
                        body: notification.body || '',
                        conversationId: notification.data && notification.data.conversationId,
                        groupId: notification.data && notification.data.groupId,
                        userId: notification.data && notification.data.userId,
                        isSupervisor: notification.data && notification.data.isSupervisor
                    };
                    
                    if (onMessageCallback) {
                        onMessageCallback(transformedNotification);
                    }
                });
                
                // Listen for errors
                PushNotifications.addListener('registrationError', function(error) {
                    console.error('Push notification error:', error);
                    if (onErrorCallback) {
                        onErrorCallback(error);
                    }
                });
            } else {
                if (onErrorCallback) {
                    onErrorCallback(new Error('Push notifications not available'));
                }
            }
        }
    };
    
    return FirebaseService;
});


